<template>
    <b-modal :visible="isOpen" :lazy="true" title-tag="h2" title="Assign Image">
        <b-row class="d-flex align-items-center form-item">
            <b-col cols="2">
                <b>Patient:</b>
            </b-col>
            <b-col>
                <span v-if="isLoadingPatient">Loading...</span>
                <v-select
                    v-else
                    v-model="selectedPatient"
                    @search="fetchPatients"
                    placeholder="Select patient (required)"
                    :options="patientListOptions"
                >
                    <template v-slot:no-options="{ search }">
                        <div>
                            <span class="no-result" v-if="search !== ''">
                                No result for "{{ search }}"
                            </span>
                            <span class="no-result" v-else>
                                Type to search
                            </span>
                        </div>
                    </template>
                </v-select>
            </b-col>
        </b-row>

        <b-row class="d-flex align-items-center form-item">
            <b-col cols="2">
                <b>Record:</b>
            </b-col>
            <b-col class="d-flex align-items-center">
                <select
                    v-model="selectedRecord"
                    :disabled="isLoadingPatient"
                    name="lab-source"
                    class="form-control"
                    aria-invalid="false"
                >
                    <option
                        v-for="item in recordList"
                        :key="item.value"
                        :value="item.value"
                    >
                        {{ item.text }}</option
                    >
                    <optgroup
                        :label="
                            'Unassigned Record (' +
                            recordUnassigned.length +
                            ')'
                        "
                    >
                        <option
                            v-for="item in recordUnassigned"
                            :key="item.value"
                            :value="item.value"
                        >
                            {{ item.text }}</option
                        >
                    </optgroup>
                    <optgroup
                        :label="
                            'Assigned Record (' + recordAssigned.length + ')'
                        "
                    >
                        <option
                            v-for="item in recordAssigned"
                            :key="item.value"
                            :value="item.value"
                        >
                            {{ item.text }}</option
                        >
                    </optgroup>
                </select>
                <spinner v-if="isLoadingRecord" />
            </b-col>
        </b-row>

        <template v-slot:modal-footer>
            <div v-if="isLoading" class="preloader">
                <img src="assets/ajax-loader.gif" />
            </div>

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading && selectedRecord !== 1"
                variant="primary"
                @click="proceed"
                :disabled="isLoadingPatient"
            >
                Assign
            </b-button>

            <b-button
                v-if="!isLoading && selectedRecord === 1"
                variant="primary"
                @click="next"
                :disabled="isLoadingPatient"
            >
                Next
            </b-button>
        </template>

        <modal-record
            ref="modalRecord"
            :hideBackdrop="true"
            @onSuccess="onSuccessModalRecord"
        />
    </b-modal>
</template>

<script>
import vSelect from "vue-select";
import { debounce, forEach } from "lodash";
import Records from "@/services/records";
import modalRecord from "@/module/commons/record/create-update";

const firstRecordItem = {
    text: "Create new record",
    value: 1
};

export default {
    components: {
        vSelect,
        modalRecord,
        Spinner: () => import("@/components/elements/spinner")
    },
    props: {
        reloadAfter: {
            type: Boolean
        },
        businessId: {
            type: String
        }
    },
    data() {
        return {
            selectedPatient: "",
            isOpen: false,
            isLoadingPatient: true,
            isLoading: false,
            patientListOptions: [],
            isDisableDropPatient: true,
            selectedRecord: 1,
            recordId: null,
            recordModule: null,
            recordList: [firstRecordItem],
            recordUnassigned: [],
            recordAssigned: [],
            isLoadingRecord: false,
            studyId: null
        };
    },
    watch: {
        selectedPatient(patient) {
            if (patient) {
                this.fetchRecords({
                    patientId: patient.id,
                    patientIndex: patient.index,
                    source: this.recordModule
                });
            }
        }
    },
    methods: {
        async open({ recordId, module, patientId, studyId }) {
            this.isOpen = true;
            this.recordId = recordId;
            this.recordModule = module;
            this.studyId = studyId;

            if (patientId) {
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}patient/${patientId}`
                );

                const patientInfo = res.data.response.resources;

                this.selectedPatient = {
                    id: patientInfo.id,
                    label: `${patientInfo.fullname} (${patientInfo.index})`,
                    value: patientInfo.id,
                    index: patientInfo.index,
                    owner:
                        patientInfo.primary_owner &&
                        patientInfo.primary_owner.length > 0
                            ? patientInfo.primary_owner[0]
                            : null
                };
            }

            this.isLoadingPatient = false;
        },
        close() {
            this.isOpen = false;
        },
        properTestName(record = {}) {
            if (record.test) {
                return record.test.display_name || record.test.name;
            } else {
                return "";
            }
        },
        async proceed() {
            try {
                this.isLoading = true;
                let formData = new FormData();
                formData.append("patient", this.selectedPatient.label);
                formData.append("patient_id", this.selectedPatient.id);
                formData.append("result_id", this.recordId);
                formData.append("record", this.selectedRecord);
                formData.append("source", this.recordModule);
                formData.append("study_id", this.studyId);

                await this.$http.post(
                    `${process.env.CI_APPS}patient/patient/save_unassigned_image`,
                    formData
                );

                // reload list
                if (this.reloadAfter) {
                    this.$emit("reloadlist");
                }

                this._message("Image successfully assigned");
                this.close();
            } catch (e) {
                this.isLoading = false;
                this._message("Error: Unable to assign image", "danger");
            }
        },
        fetchPatients(search) {
            this.searchPatients(search, this);
        },
        async fetchRecords({ patientIndex, patientId, source }) {
            this.isLoadingRecord = true;
            const res = await this.$http.get(
                `${process.env.CI_APPS}patient/patient/find_records`,
                {
                    params: {
                        id: patientIndex,
                        pid: patientId,
                        source: source
                    }
                }
            );

            let options = res.data.options;
            if (options) {
                if (_.isArray(options.assigned)) {
                    forEach(options.assigned, option => {
                        this.recordAssigned.push({
                            value: option.id,
                            text: option.name
                        });
                    });
                }

                if (_.isArray(options.unassigned)) {
                    forEach(options.unassigned, option => {
                        this.recordUnassigned.push({
                            value: option.id,
                            text: option.name
                        });
                    });
                }
            }

            this.isLoadingRecord = false;
        },
        searchPatients: debounce(async (search, self) => {
            if (search === "" || search.length < 2) {
                return;
            }

            const res = await self.$http.get(
                `${process.env.BARRAMUNDI}patient/search-patient`,
                {
                    params: {
                        query: search
                    }
                }
            );

            const { message, resources } = res.data.response;

            if (message.code === 200) {
                self.patientListOptions = self.generatePatientList(resources);
                self.isDisableDropPatient = false;
            } else {
                self.patientListOptions = [];
            }
        }, 350),
        generatePatientList(records = []) {
            let result = [];

            for (const record of records) {
                result.push({
                    label: `${record.fullname} (${record.index})`,
                    id: record.id,
                    value: record.id,
                    index: record.index,
                    owner: record.owner ? record.owner : null
                });
            }

            return result;
        },
        next() {
            Records.getCreateDetails(this.selectedPatient.id).then(response => {
                if (response) {
                    this.$refs.modalRecord.open({
                        action: "assign_image",
                        module: this.MODULE.PATIENT,
                        patient: {
                            id: this.selectedPatient.id
                        },
                        clientID: this.selectedPatient.owner
                            ? this.selectedPatient.owner.id
                            : null,
                        lab_data: {
                            result_id: this.recordId,
                            reference_type: this.recordModule,
                            item_type: 17
                        },
                        create_resource: response.form_data
                            ? response.form_data
                            : null,
                        studyId: this.studyId
                    });
                }
            });
        },
        onSuccessModalRecord() {
            if (this.reloadAfter) {
                this.$emit("reloadlist");
            }
            this.close();
        }
    }
};
</script>

<style scoped>
.preloader {
    margin: 8px 0;
}
.form-item {
    margin-bottom: 10px;
}
span.no-result {
    margin-left: 10px;
}
</style>
